import type {CmLogger} from "@cm/log"
import {keyForMeshMaterialData} from "#template/interfaces/material-data"
import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import {GlobalRenderConstants, SceneNodes} from "#template/interfaces/scene-object"
import {CachedEvaluator} from "@cm/graph-v3"
import type {
    CyclesContext,
    DataResolver,
    ImageGenerator,
    IMaterialData,
    MaterialGraphCyclesReturns,
    MeshRenderSettings,
    TextureResolution,
} from "@cm/material-nodes"
import {Emission, Geometry, materialConnection, MaterialGraph, OutputMaterial, VectorMath} from "@cm/material-nodes"
import type {Vector3} from "@cm/math"
import {Matrix4} from "@cm/math"
import type {DataNodes, MeshNodes} from "@cm/render-nodes"
import {RenderNodes} from "@cm/render-nodes"
import type {Color} from "@cm/render-nodes/shader-expr"
import {ShaderExpr} from "@cm/render-nodes/shader-expr"
import type {Observable} from "rxjs"

export type RenderGraphResolveFunctions = {
    getHdriDataObjectIdDetails(hdriIdDetails: IdDetails): Promise<IdDetails>
}

export function getMeshGraphInfo(graph: MeshNodes.Mesh) {
    const dataObjectReferences: DataNodes.DataObjectReference[] = []
    let maxSubdivLevel = 0
    const visited = new Set<unknown>()
    const traverseMeshData = (node: MeshNodes.Mesh) => {
        if (visited.has(node)) return
        visited.add(node)
        if (node.type === "geomGraph") {
            traverseGeomExpr(node.graph)
        } else if (node.type === "loadMesh") {
            if (node.data.type === "dataObjectReference") {
                dataObjectReferences.push(node.data)
            }
        } else if (node.type === "subdivide") {
            maxSubdivLevel = Math.max(maxSubdivLevel, node.levels)
            traverseMeshData(node.input)
        }
    }
    const traverseGeomExpr = (node: MeshNodes.GeometryExpr | MeshNodes.GeometryExprArg | MeshNodes.GeometryExprArg[]) => {
        if (visited.has(node)) return
        visited.add(node)
        if (Array.isArray(node)) {
            for (const subNode of node) {
                traverseGeomExpr(subNode)
            }
        } else if (typeof node === "object") {
            if (node.op === "meshToGeom") {
                traverseMeshData((node as MeshNodes.MeshToGeometry).args[0])
            } else {
                for (const arg of (node as MeshNodes.GeometryOperator).args) {
                    if (typeof arg === "object") {
                        traverseGeomExpr(arg)
                    }
                }
            }
        }
    }
    traverseMeshData(graph)
    return {
        dataObjectReferences,
        maxSubdivLevel,
    }
}

function sanitizeCryptoMatteName(name: string | undefined): string | undefined {
    // Cycles does not properly escape quoted strings in the cryptomatte manifest, so we need to strip them here to avoid breaking the render engine JSON parser.
    // (This should be fixed in newer builds of the render server, so keep this here as a temporary workaround)
    return name ? name.replace(/["\\\t\n\r\f\b]/g, "") : name
}

function cached<S, R>(originalMethod: (node: S) => Promise<R>): (node: S) => Promise<R> {
    const circularRef = Symbol("circularRef")
    const cache = new Map<S, R | typeof circularRef>()

    return async function (node: S): Promise<R> {
        const cachedValue = cache.get(node)
        if (cachedValue !== undefined) {
            if (cachedValue === circularRef) throw Error("Circular reference detected")
            return cachedValue
        }

        cache.set(node, circularRef)

        const result = await originalMethod(node)
        cache.set(node, result)

        return result
    }
}

type IdDetails = {
    legacyId: number
}

// Cryptomatte masks for the compositing pipeline. Each adds 4 channels to the frame buffer's pass stride,
// so a render that does not composite should not ask for them.
export const COMPOSITING_PASSES: readonly RenderNodes.PassName[] = [
    "Combined",
    "CryptoObject00",
    // 'CryptoObject01',
    // 'CryptoObject02',
    "CryptoMaterial00",
    // 'CryptoMaterial01',
    // 'CryptoMaterial02',
    "CryptoAsset00",
    // 'CryptoAsset01',
    // 'CryptoAsset02',
]

type RenderGraphBuilderInput = {
    nodes: SceneNodes.SceneNode[]
    fullQuality: boolean
    renderPurpose: RenderNodes.RenderPurpose
    aoMaskPass?: boolean
    verifySchema: boolean
    passes: readonly RenderNodes.PassName[]
    materialCache?: Map<string, Promise<MaterialGraphCyclesReturns>>
    registerImageDataObject?: CyclesContext["registerImageDataObject"]
    textureResolution?: TextureResolution
}

class RenderGraphBuilder {
    constructor(
        private resolve: DataResolver,
        private input: RenderGraphBuilderInput,
        private readonly logger: CmLogger,
    ) {}

    private materialCache = new Map<string, Promise<MaterialGraphCyclesReturns>>()
    private renderSettingsNode!: SceneNodes.RenderSettings

    async build(): Promise<RenderNodes.Render> {
        const {nodes, fullQuality, renderPurpose, verifySchema, aoMaskPass, passes} = this.input

        const renderNode = nodes.find(SceneNodes.RenderSettings.is)
        if (!renderNode) throw Error("Cannot find render node")
        this.renderSettingsNode = renderNode

        const cameraNode = nodes.find(SceneNodes.Camera.is)
        if (!cameraNode) throw Error("Cannot find camera node")

        const rootRenderNode: RenderNodes.Render = {
            type: "render",
            schemaVersion: 1,
            session: {
                options: {
                    // threads: Math.floor(navigator.hardwareConcurrency * 0.9),
                    // use_path_guiding: false,
                    // use_light_tree: false,
                    cloud: renderNode.cloud,
                    gpu: renderNode.gpu,
                    texture_streaming: renderNode.textureStreaming || undefined,
                    render_tiling: renderNode.renderTiling,
                    denoise_tiling: renderNode.denoiseTiling,
                    transparent_background: true,
                    transparent_glass: true,
                    // BACKWARDS COMPAT (final_render): render servers update independently of the web app, so a
                    // graph may be claimed by an engine older than the frontend that built it. render_purpose is
                    // authoritative; this is what an older engine reads. Remove once every deployed engine
                    // reads render_purpose.
                    final_render: renderPurpose !== "interactive",
                    render_purpose: renderPurpose,
                    use_denoising: true,
                    denoise_use_gpu: true,
                    adaptive_subdivision_offscreen_dicing_scale: 256.0,
                    passes: [...passes], // setPassEnabled mutates this list; never alias the caller's array
                },
            },
            scene: {
                camera: {
                    transform: cameraNode.transform.toArray(),
                    shiftX: cameraNode.shiftX,
                    shiftY: cameraNode.shiftY,
                    nearClip: cameraNode.nearClip,
                    farClip: cameraNode.farClip,
                    fStop: cameraNode.fStop,
                    focalLength: cameraNode.focalLength,
                    focalDistance: cameraNode.focalDistance,
                    sensorSize: cameraNode.filmGauge,
                    exposure: cameraNode.exposure,
                },
                objects: [],
                lights: [],
            },
            width: Math.round(cameraNode.resolutionX * (renderNode.percentage / 100)),
            height: Math.round(cameraNode.resolutionY * (renderNode.percentage / 100)),
            samples: Math.round(renderNode.samples),
        }

        const optionsNode = nodes.find(SceneNodes.SceneOptions.is)
        const scene = rootRenderNode.scene

        const createMeshData = cached(async (node: CompleteMeshData): Promise<MeshNodes.Mesh> => {
            return node.abstract.renderGeometryGraph
        })

        let shadowCatcherUsed = false
        const createMeshObject = cached(async (node: SceneNodes.Mesh): Promise<RenderNodes.Mesh | null> => {
            if (aoMaskPass && !node.visibleDirectly) {
                // (default is true, so compare to explicit false value)
                // NOTE: just setting visibleToShadowRays to false for non-shadow catcher meshes doesn't work with environment lights.
                // (Cycles considers this case as a diffuse ray, apparently)
                // just skip this mesh as it should not contribute to the shadow mask
                return null
            }

            const meshData = await createMeshData(node.completeMeshData)
            const meshDataInfo = getMeshGraphInfo(meshData)

            const shaders: RenderNodes.Mesh["shaders"] = {}

            const mesh: RenderNodes.Mesh = {
                type: "mesh",
                id: node.id,
                transform: node.transform.toArray(),
                meshData,
                shaders,
                visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                visibleInReflections: node.visibleInReflections ? undefined : false,
                visibleInRefractions: node.visibleInRefractions ? undefined : false,
                cryptomatteObjectName: sanitizeCryptoMatteName(node.meshRenderSettings.cryptoMatteObjectName),
                cryptomatteAssetName: sanitizeCryptoMatteName(node.meshRenderSettings.cryptoMatteAssetName),
            }
            if (aoMaskPass) {
                // Hide normal meshes from the camera so only the shadow catcher (mask) is visible
                mesh.visibleInCamera = false
            }

            for (const [slot, materialData] of node.materialMap?.entries() ?? []) {
                if (!materialData) {
                    shaders[slot] = RenderNodes.defaultMaterial(slot)
                } else {
                    //TODO: cache converted materials
                    if (optionsNode?.enableAdaptiveSubdivision && (this.materialHasDisplacement(materialData) || node.meshRenderSettings.displacementImage)) {
                        if (meshDataInfo.maxSubdivLevel == 0) {
                            this.logger.warn("Enabling adaptive subdivision for mesh with subdivLevel = 0")
                        }
                        mesh.adaptiveSubdivisionRate = 2.0
                        this.logger.log(`Enabling adaptive subdivision for material ${materialData.name}`)
                    }
                    // real materials must be used even for aoMask pass, because they may have transparency
                    //TODO: check for mixed shadow catcher / non shadow catcher materials on mesh
                    let shader = await this.convertMeshMaterial(materialData, node, createMeshData, getObjects)
                    if (!shader) continue

                    if (materialData.materialGraph.rootNode?.parameters.settings.shadowCatcher) {
                        mesh.shadowCatcher = true
                        shadowCatcherUsed = true
                        if (aoMaskPass) {
                            mesh.visibleToShadowRays = false
                            mesh.visibleInCamera = true
                        }
                    }

                    shader.parameters.name = sanitizeCryptoMatteName(materialData.name) // this is used as the cryptomatte material name
                    shaders[slot] = shader
                }
            }

            return mesh
        })

        const createSeamObjects = cached(async (node: SceneNodes.Seam): Promise<RenderNodes.MeshInstances[]> => {
            const result: RenderNodes.MeshInstances[] = []

            if (!node.completeCurveData.reified) return []

            const {points, normals, tangents, scales} = node.completeCurveData.reified

            const curvePoints = {
                points: Array.from(points),
                normals: Array.from(normals),
                tangents: Array.from(tangents),
                scales: Array.from(scales),
            }

            const {meshCurveControl} = node
            const meshNode = nodes.find((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x) && x.id === meshCurveControl.mesh.id) ?? meshCurveControl.mesh

            const meshData = await createMeshData(meshNode.completeMeshData)

            for (const currentItem of node.item) {
                const currentItemNode = await createMeshObject(currentItem)
                if (!currentItemNode) continue

                result.push({
                    type: "meshInstances",
                    id: `${node.id}_${meshCurveControl.id}_${currentItem.id}`,
                    mesh: currentItemNode,
                    transform: node.transform.toArray(),
                    instances: {
                        type: "curveIntersections",
                        meshData,
                        curvePoints,
                    },
                })
            }

            return result
        })

        const getObjects = async (node: SceneNodes.SceneNode): Promise<RenderNodes.Object[]> => {
            if (SceneNodes.Mesh.is(node)) {
                const mesh = await createMeshObject(node)
                if (mesh) return [mesh]
                else return []
            } else if (SceneNodes.Seam.is(node)) {
                const meshInstances = await createSeamObjects(node)
                return meshInstances
            } else return []
        }

        for (const node of nodes) {
            if (SceneNodes.Mesh.is(node)) {
                const mesh = await createMeshObject(node)
                if (mesh) scene.objects.push(mesh)
            } else if (SceneNodes.Seam.is(node)) {
                const meshInstances = await createSeamObjects(node)
                scene.objects.push(...meshInstances)
            } else if (SceneNodes.AreaLight.is(node)) {
                // emissive mesh:
                //TODO: For an emissive mesh to work with shadow catcher objects, it must _also_ be set as a shadow catcher, and thus be invisible to the camera. This seems like a Cycles design issue...
                // scene.objects.push({
                //     id: node.id,
                //     transform: node.transform.toArray(),
                //     meshData: {
                //         type: 'plane',
                //         width: node.width,
                //         height: node.height,
                //         normalAxis: 'z-',
                //     },
                //     shaders: {
                //         0: this.createAreaLightShader(node.color, node.on ? (node.intensity * GlobalRenderConstants.lightIntensityScale) : 0, node.directionality, node.transparent)
                //     },
                //     visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                //     visibleInReflections: node.visibleInReflections ? undefined : false,
                //     visibleInRefractions: node.visibleInRefractions ? undefined : false,
                // });

                if (node.on && !aoMaskPass) {
                    scene.lights.push({
                        type: "area",
                        id: node.id,
                        transform: node.transform.toArray(),
                        width: node.width,
                        height: node.height,
                        strength: 1.0, // strength is set in the shader
                        directionality: node.directionality ?? 0,
                        visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                        visibleInReflections: node.visibleInReflections ? undefined : false,
                        visibleInRefractions: node.visibleInRefractions ? undefined : false,
                        shader: await this.createEmissionShader(
                            `Emission Shader ${node.id}`,
                            node.color,
                            node.intensity * GlobalRenderConstants.lightIntensityScale,
                            node.texture,
                            createMeshData,
                            getObjects,
                            verifySchema,
                        ),
                    })
                }
                if (!node.transparent && !aoMaskPass) {
                    scene.objects.push({
                        type: "mesh",
                        id: node.id + "-mesh",
                        transform: node.transform.multiply(Matrix4.translation(0, 0, 0.1)).toArray(), // offset 1mm along z+ axis
                        meshData: {
                            type: "plane",
                            width: node.width,
                            height: node.height,
                            normalAxis: "z-",
                        },
                        shaders: {
                            0: this.createNullShader(),
                        },
                        visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                        visibleInReflections: node.visibleInReflections ? undefined : false,
                        visibleInRefractions: node.visibleInRefractions ? undefined : false,
                    })
                }
            } else if (SceneNodes.PointLight.is(node)) {
                if (node.on && !aoMaskPass) {
                    scene.lights.push({
                        type: "point",
                        id: node.id,
                        transform: node.transform.toArray(),
                        radius: node.radius,
                        strength: 1.0, // strength is set in the shader
                        visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                        visibleInReflections: node.visibleInReflections ? undefined : false,
                        visibleInRefractions: node.visibleInRefractions ? undefined : false,
                        shader: await this.createEmissionShader(
                            `Emission Shader ${node.id}`,
                            node.color,
                            node.intensity * GlobalRenderConstants.lightIntensityScale,
                            undefined,
                            createMeshData,
                            getObjects,
                            verifySchema,
                        ),
                    })
                }
            } else if (SceneNodes.SunLight.is(node)) {
                if (node.on && !aoMaskPass) {
                    scene.lights.push({
                        type: "sun",
                        id: node.id,
                        transform: node.transform.toArray(),
                        angle: node.angle,
                        strength: 1.0, // strength is set in the shader
                        visibleInCamera: node.visibleDirectly ? undefined : false, // omit true (default) to optimize graph JSON size
                        visibleInReflections: node.visibleInReflections ? undefined : false,
                        visibleInRefractions: node.visibleInRefractions ? undefined : false,
                        shader: await this.createEmissionShader(
                            `Emission Shader ${node.id}`,
                            node.color,
                            node.intensity,
                            undefined,
                            createMeshData,
                            getObjects,
                            verifySchema,
                        ),
                    })
                }
            } else if (SceneNodes.LightPortal.is(node)) {
                if (!aoMaskPass) {
                    scene.lights.push({
                        type: "portal",
                        id: node.id,
                        transform: node.transform.toArray(),
                        width: node.width,
                        height: node.height,
                    })
                }
            } else if (SceneNodes.Environment.is(node)) {
                if (!aoMaskPass) {
                    if (node.envData?.type === "dataObject") {
                        const {legacyId} = node.envData.dataObject
                        const imageNode = RenderNodes.loadImage(legacyId)
                        scene.environment = this.createEnvironmentShader(imageNode, node.rotation, node.intensity, node.saturation / 100.0, node.mirror)
                    } else {
                        this.logger.warn("TODO: create env map shader from URL")
                    }
                }
            }
        }

        if (aoMaskPass) {
            rootRenderNode.session.options["max_bounces"] = 0
            scene.environment = this.createUniformEnvironmentShader()
            rootRenderNode.session.options["passes"] = ["ShadowCatcher"]
        } else {
            const setPassEnabled = (name: RenderNodes.PassName, enabled: boolean) => {
                const list = rootRenderNode.session.options.passes ?? []
                const idx = list.indexOf(name)
                if (enabled && idx === -1) {
                    list.push(name)
                } else if (!enabled && idx !== -1) {
                    list.splice(idx, 1)
                }
            }
            setPassEnabled("ShadowCatcher", shadowCatcherUsed)
        }

        if (verifySchema) {
            // try to catch potentially invalid render graph early, before the job submission
            // this seems to be quite expensive... so allow for it to be disabled
            const parsedGraph = RenderNodes.RenderSchema.safeParse(rootRenderNode)
            if (!parsedGraph.success) throw new Error(`Failed to parse render graph: ${parsedGraph.error.errors.map((e) => e.message).join(", ")}`)
            return rootRenderNode //We should not use the parsed graph here, as identity of objects is important
        } else {
            return rootRenderNode
        }
    }

    private createNullShader(): RenderNodes.ShaderNode {
        return ShaderExpr.materialOutput(null).compile()
    }

    private async createEmissionShader(
        name: string,
        color: [number, number, number],
        strength: number,
        texture: ImageGenerator | undefined,
        getMeshData: (node: CompleteMeshData) => Promise<MeshNodes.Mesh>,
        getObjects: (node: SceneNodes.SceneNode) => Promise<RenderNodes.Object[]>,
        verifySchema: boolean,
    ): Promise<RenderNodes.ShaderNode> {
        const getEmission = (): Emission => {
            if (texture) {
                const multiplier = new VectorMath({
                    parameters: {
                        inputs: {
                            vector: texture.imageNode.generator({
                                uv: materialConnection(
                                    new Geometry({
                                        parameters: {
                                            inputs: {},
                                            settings: {},
                                        },
                                    }),
                                    "parametric",
                                ),
                                extension: "REPEAT",
                                interpolation: "Linear",
                                projection: "FLAT",
                            }).color,
                        },
                        settings: {
                            operation: "MULTIPLY",
                            vector_001: {x: color[0], y: color[1], z: color[2]},
                        },
                    },
                })

                const emission = new Emission({
                    parameters: {
                        inputs: {
                            color: materialConnection(multiplier, "vector"),
                        },
                        settings: {
                            strength,
                        },
                    },
                })

                return emission
            } else {
                const emission = new Emission({
                    parameters: {
                        inputs: {},
                        settings: {
                            color: {r: color[0], g: color[1], b: color[2]},
                            strength,
                        },
                    },
                })

                return emission
            }
        }

        const output = new OutputMaterial({
            parameters: {
                inputs: {
                    surface: materialConnection(getEmission(), "emission"),
                },
                settings: {},
            },
        })

        return this.convertMaterial(
            {
                name,
                materialGraph: MaterialGraph.fromOutputMaterial(output),
                side: "front",
            },
            undefined,
            getMeshData,
            getObjects,
        )
    }

    private createEnvironmentShader(
        image: RenderNodes.Image,
        rotation: Vector3,
        intensity: number,
        saturation: number,
        mirror?: boolean,
    ): RenderNodes.ShaderNode {
        //TODO: enabling importance sampling by default introduces extra noise when the env map is not the primary source of light. Perhaps use (intensity > 0) as the condition for enabling it?
        const degToRad = Math.PI / 180
        const map1 = ShaderExpr.mapping(ShaderExpr.geometryInfo().position, {
            rotation: [rotation.x * degToRad, rotation.y * degToRad, rotation.z * degToRad],
            scale: mirror ? [-1, 1, 1] : [1, 1, 1],
        })
        const map2 = ShaderExpr.mapping(map1, {rotation: [Math.PI / 2, 0.0, 0.0]})
        const tex = ShaderExpr.environmentTexture(map2, image)
        const hsv = ShaderExpr.hsv(tex, 1.0, 0.5, saturation, 1.0)
        const brdf = ShaderExpr.backgroundBRDF(hsv, intensity)
        return ShaderExpr.materialOutput(brdf, {use_mis: intensity > 0}).compile()
    }

    private createUniformEnvironmentShader(color: Color = [1, 1, 1], intensity = 1.0): RenderNodes.ShaderNode {
        const brdf = ShaderExpr.backgroundBRDF(ShaderExpr.color(color), intensity)
        return ShaderExpr.materialOutput(brdf).compile()
    }

    private materialHasDisplacement(materialData: IMaterialData): boolean {
        const rootNode = materialData.materialGraph.rootNode
        if (rootNode === null) return false
        return rootNode.parameters.inputs.displacement !== undefined && rootNode.parameters.settings.displacementEnabled !== false
    }

    private async convertMaterial(
        materialData: IMaterialData,
        meshRenderSettings: MeshRenderSettings | undefined,
        getMeshData: (node: CompleteMeshData) => Promise<MeshNodes.Mesh>,
        getObjects: (node: SceneNodes.SceneNode) => Promise<RenderNodes.Object[]>,
    ): Promise<MaterialGraphCyclesReturns> {
        const {verifySchema, textureResolution: inputTextureResolution} = this.input

        const textureResolution = inputTextureResolution ?? "original"
        const textureStreamingEnabled = this.renderSettingsNode.textureStreaming

        const key = `${keyForMeshMaterialData(materialData, meshRenderSettings)}-${textureResolution}-${textureStreamingEnabled}`

        const cache = this.input.materialCache ?? this.materialCache

        const cached = cache.get(key)
        if (cached !== undefined) return cached

        const cyclesContext: CyclesContext = {
            addTask: (arg: {description: string; task: Observable<any>; critical: boolean}) => {},
            forceFiltering: undefined,
            resolve: this.resolve,
            textureResolution,
            textureStreamingEnabled,
            meshRenderSettings,
            getMeshData: (node) => getMeshData(node as CompleteMeshData),
            getObjects: (node) => getObjects(node as SceneNodes.SceneNode),
            registerImageDataObject: this.input.registerImageDataObject,
            type: "cycles",
            logger: this.logger,
        }
        const evaluator = new CachedEvaluator<CyclesContext>(cyclesContext, !verifySchema)
        const material = materialData.materialGraph.compileToCycles(evaluator)
        cache.set(key, material)
        return material
    }

    private async convertMeshMaterial(
        materialData: IMaterialData,
        mesh: SceneNodes.Mesh,
        getMeshData: (node: CompleteMeshData) => Promise<MeshNodes.Mesh>,
        getObjects: (node: SceneNodes.SceneNode) => Promise<RenderNodes.Object[]>,
    ): Promise<MaterialGraphCyclesReturns | null> {
        const {fullQuality} = this.input
        if (!materialData?.materialGraph || (!fullQuality && materialData.realtimeSettings?.disable)) return null
        const {meshRenderSettings} = mesh
        return this.convertMaterial(materialData, meshRenderSettings, getMeshData, getObjects)
    }
}

export function buildRenderGraph(data: RenderGraphBuilderInput, resolve: DataResolver, logger: CmLogger): Promise<RenderNodes.Render> {
    return new RenderGraphBuilder(resolve, data, logger).build()
}
